import { useQuery } from '@tanstack/react-query'
import { api } from '../../../lib/api'
import type { Entity } from '../../../types'

/* ───── Extended Entity Detail (superset of base Entity) ───── */

export interface PhoneAppData {
  is_admin: boolean
  is_business: boolean
  verified: boolean
  premium: boolean
  bot: boolean
  phone_status: string | null
  about: string | null
  device_type: string | null
  country_code: string | null
}

export interface EntityAppData {
  scraping_id: string
  sqid?: string
  entity_type: string
  first_seen: string
  last_updated: string
  created_at?: string
  updated_at?: string
  classification_tags: string[]
  risk_breakdown: { label: string; value: number }[]
  raw_metadata: Record<string, unknown>
  phone_app_data?: PhoneAppData | null

  // Additional metadata fields (migration 000055)
  sub_classifications?: string | null
  relevancy_score?: number | null
  creator?: string | null
  last_updater?: string | null
  source?: string | null
  context?: string | null
  enrichment_status?: string | null
  identifier_type?: string | null
  last_scrape_time?: string | null
  last_monitoring_time?: string | null
}

export interface PersonalDetailSource {
  source: string
  records: {
    name: string
    username: string
    bio: string
    phone: string
    email: string
    profile_image_url: string
    scraped_at: string
    displayname: string
    gender: string
    education: string
    languages: string
    notes_json: string | null
  }[]
}

export interface EntityAccount {
  platform: string
  platform_user_id: string
  username: string
  display_name: string
  profile_url: string
  enrichment_status: 'pending' | 'completed' | 'failed' | 'none'
  last_enriched_at: string | null
}

export interface EntityDetail extends Entity {
  /** Profile image URL (may be empty) */
  profile_image_url: string
  /** Primary platform */
  platform: string
  /** Last active timestamp */
  last_active: string
  /** Phone number */
  phone?: string
  /** Platform username */
  username?: string
  /** Display nickname */
  nickname?: string
  /** First seen timestamp */
  first_seen?: string
  /** Last seen timestamp */
  last_seen?: string
  /** Allow additional dynamic fields from the API */
  [key: string]: unknown
}

interface EntityDetailResponse {
  entity: EntityDetail
}

interface EntityAppDataResponse {
  app_data: EntityAppData
}

interface PersonalDetailsResponse {
  personal_details: PersonalDetailSource[]
  images: string[]
}

interface AccountsResponse {
  accounts: EntityAccount[]
}

/* ───── Query Keys ───── */

export const entityDetailQueryKey = (id: string) => ['entity', id] as const
export const entityAppDataQueryKey = (id: string) => ['entity', id, 'app-data'] as const
export const entityPersonalDetailsQueryKey = (id: string) =>
  ['entity', id, 'personal-details'] as const
export const entityAccountsQueryKey = (id: string) => ['entity', id, 'accounts'] as const

/* ───── Hooks ───── */

/**
 * TanStack Query hook for fetching entity detail.
 * GET /api/v1/entities/{id}
 */
export function useEntityDetail(id: string) {
  const query = useQuery({
    queryKey: entityDetailQueryKey(id),
    queryFn: ({ signal }) =>
      api<EntityDetailResponse>(`/v1/entities/${id}`, { signal }),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  return {
    entity: query.data?.entity ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * TanStack Query hook for entity app data tab.
 * GET /api/v1/entities/{id}/app-data
 */
export function useEntityAppData(id: string) {
  const query = useQuery({
    queryKey: entityAppDataQueryKey(id),
    queryFn: ({ signal }) =>
      api<EntityAppDataResponse>(`/v1/entities/${id}/app-data`, { signal }),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  })

  return {
    appData: query.data?.app_data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  }
}

/**
 * TanStack Query hook for entity personal details tab.
 * GET /api/v1/entities/{id}/personal-details
 */
export function useEntityPersonalDetails(id: string) {
  const query = useQuery({
    queryKey: entityPersonalDetailsQueryKey(id),
    queryFn: ({ signal }) =>
      api<PersonalDetailsResponse>(`/v1/entities/${id}/personal-details`, { signal }),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  })

  return {
    details: query.data?.personal_details ?? [],
    images: query.data?.images ?? [],
    isLoading: query.isLoading,
    error: query.error,
  }
}

/**
 * TanStack Query hook for entity accounts tab.
 * GET /api/v1/entities/{id}/accounts
 */
export function useEntityAccounts(id: string) {
  const query = useQuery({
    queryKey: entityAccountsQueryKey(id),
    queryFn: ({ signal }) =>
      api<AccountsResponse>(`/v1/entities/${id}/accounts`, { signal }),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  })

  return {
    accounts: query.data?.accounts ?? [],
    isLoading: query.isLoading,
    error: query.error,
  }
}
